// One visual vocabulary for the "card grid" surfaces under /website
// (main banner, themes gallery, landing pages). Importing these class lists
// instead of re-typing them per feature is what keeps the three grids reading
// as a single screen rather than three mini-dashboards with their own styles.

/** Outer card surface — border, radius, white/dark fill, hover lift. */
export const cardSurface =
  "group relative flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm shadow-gray-900/[0.03] transition-all duration-200 hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-md hover:shadow-purple-900/10 dark:border-gray-700/70 dark:bg-gray-800 dark:shadow-black/20 dark:hover:border-purple-700/60";

/** Selected/active state — append on top of `cardSurface` in picker contexts. */
export const cardSurfaceSelected =
  "border-purple-600 ring-2 ring-purple-600/25 dark:border-purple-400 dark:ring-purple-400/25";

/** Media frame sitting at the top of a card (image area). Add an aspect class. */
export const cardMedia =
  "relative w-full overflow-hidden bg-gray-50 dark:bg-gray-900/40";

/** An <img> filling a `cardMedia` frame. */
export const cardMediaImg =
  "h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]";

/** Bottom scrim so an overlaid title stays legible over any image. */
export const cardScrim =
  "pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/65 via-black/15 to-transparent";

/** Title overlaid on the bottom of a media frame. */
export const cardOverlayTitle =
  "absolute inset-x-0 bottom-0 line-clamp-2 px-4 pb-3 text-sm font-bold leading-snug tracking-tight text-white drop-shadow";

/** Frosted pill that floats over a media frame (order #, counts, …). */
export const cardFloatChip =
  "inline-flex h-7 min-w-7 items-center justify-center rounded-lg bg-white/90 px-2 text-[12px] font-bold tabular-nums text-purple-900 shadow-sm ring-1 ring-purple-100 backdrop-blur dark:bg-gray-900/80 dark:text-purple-300 dark:ring-purple-700/40";

/** Footer strip holding the row actions, separated from the body by a hairline. */
export const cardFooter =
  "mt-auto flex flex-wrap items-center gap-2 border-t border-gray-100 px-3 py-3 dark:border-gray-700/60";

/** Skeleton placeholder card shell (matches `cardSurface`, minus the hover). */
export const cardSkeletonSurface =
  "overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700/70 dark:bg-gray-800";

/** Generic shimmering block used inside skeletons. */
export const cardSkeletonBlock = "animate-pulse rounded bg-gray-200 dark:bg-gray-700";

/** Dashed container for empty / no-results states. */
export const cardEmptySurface =
  "rounded-2xl border border-dashed border-gray-200 bg-gray-50/40 dark:border-gray-700 dark:bg-gray-900/30";

/** Pagination / "load more" footer wrapper placed under a card grid. */
export const cardFooterBar =
  "overflow-hidden rounded-2xl bg-white dark:bg-gray-800";

/** Gap shared by every card grid (1 column on phones, denser above). */
export const cardGridGap = "gap-4 sm:gap-5";

/** Vertical rhythm between a card grid and its footer bar. */
export const cardGridStack = "space-y-5";

/**
 * Tinted "content zone" that holds a card grid below the page hero / filters —
 * bleeds to the edges of a `table-card` and rounds its bottom to match.
 */
export const cardZone =
  "-mx-3 mt-5 rounded-b-2xl border-t border-gray-100 bg-gray-50/60 px-3 pb-5 pt-5 dark:border-gray-700/60 dark:bg-gray-900/30 md:-mx-4 md:px-4 md:pb-6";
